import { API as RestAPI } from "aws-amplify";
import { AppContextValue } from "../setup/context-value";
import { APIResponse, toDefaultRequestPayload } from "./common";

export interface CreateKBOutput {
    instanceId: string;
    message: string;
}

export interface DeleteKBOutput {
    instanceId: string;
    message: string;
}

export interface GetKBOutput {
    instanceId: string;
    knowledgeBaseId?: string;
    knowledgeBaseArn?: string;
    dataSourceId?: string;
    /**
     * `NOT_PROVISIONED` when the instance carries no Knowledge Base id (this is also what a
     * provisioning run that is still in flight reads as, until the ids are persisted),
     * `NOT_FOUND` when the recorded id is stale, otherwise Bedrock's own status string
     * (ACTIVE / CREATING / UPDATING / DELETING / FAILED).
     */
    status: string;
}

export interface TriggerReSyncOutput {
    instanceId: string;
    message: string;
}

export interface KBSyncStatusItem {
    /** The page id (also the Knowledge Base document id). */
    id: string;
    instanceId: string;
    action: "INGESTED" | "DELETED";
    /** The published page version whose content was ingested; absent on deletes. */
    pageVersion?: number;
    pageTitle?: string;
    documentStatus?: string;
    syncDate: string;
}

export interface ListKBSyncStatusOutput {
    instanceId: string;
    /** Last sync outcome per page, most recent first. Pages never synced have no row. */
    statuses: KBSyncStatusItem[];
}

export interface KBMcpToolOutput {
    instanceId: string;
    /** READY: tool callable. NOT_CREATED: gateway up, no tool. GATEWAY_MISSING: stage lacks the stack. */
    status: "READY" | "CREATING" | "NOT_CREATED" | "GATEWAY_MISSING";
    gatewayName: string;
    /** The AgentCore Gateway MCP endpoint clients connect to. */
    gatewayUrl?: string;
    toolName: string;
    targetStatus?: string;
    /** Quick "Service authentication" values (Cognito client credentials), when READY. */
    tokenUrl?: string;
    clientId?: string;
    clientSecret?: string;
}

export interface AITeammateAccessOutput {
    instanceId: string;
    /** The per-instance role AI Teammate assumes; undefined when nothing is connected. */
    accessRoleArn?: string;
    trustedTeammateArns: string[];
}

/**
 * Knowledge Base API client for the per-instance Bedrock Knowledge Base ("PathFinder KB").
 * Every operation requires CAN_ADMIN on the instance, enforced server-side by
 * KnowledgeBaseAPI in AskLegalLambda — the admin portal tab is a UX gate only.
 *
 * createKB / deleteKB are asynchronous server-side: they return as soon as provisioning or
 * deletion has been started, so callers poll getKB for the outcome. The AI Teammate access
 * operations are synchronous — they manage the per-instance IAM role AI Teammate assumes.
 */
export interface KnowledgeBaseAPI {
    createKB(instanceId: string): Promise<APIResponse<CreateKBOutput>>;
    deleteKB(instanceId: string): Promise<APIResponse<DeleteKBOutput>>;
    getKB(instanceId: string): Promise<APIResponse<GetKBOutput>>;
    /**
     * Fire-and-forget like createKB/deleteKB, but with nothing to poll: the run is idempotent
     * and re-runnable, so the UI only confirms it started.
     */
    triggerReSync(instanceId: string): Promise<APIResponse<TriggerReSyncOutput>>;
    listKBSyncStatus(instanceId: string): Promise<APIResponse<ListKBSyncStatusOutput>>;
    /** Per-instance MCP tool on the stage's KB MCP AgentCore Gateway. All synchronous. */
    createKBMcpTool(instanceId: string): Promise<APIResponse<KBMcpToolOutput>>;
    deleteKBMcpTool(instanceId: string): Promise<APIResponse<KBMcpToolOutput>>;
    getKBMcpTool(instanceId: string): Promise<APIResponse<KBMcpToolOutput>>;
    connectAITeammate(instanceId: string, teammateRoleArn: string): Promise<APIResponse<AITeammateAccessOutput>>;
    /** Omit `teammateRoleArn` to disconnect all teammates and delete the access role. */
    disconnectAITeammate(instanceId: string, teammateRoleArn?: string): Promise<APIResponse<AITeammateAccessOutput>>;
    getAITeammateAccess(instanceId: string): Promise<APIResponse<AITeammateAccessOutput>>;
}

export class KnowledgeBaseAPIImpl implements KnowledgeBaseAPI {
    constructor(private context: AppContextValue) { }

    async createKB(instanceId: string): Promise<APIResponse<CreateKBOutput>> {
        const response: APIResponse<CreateKBOutput> = await RestAPI.post(
            "custom-domain",
            "/knowledge-base/create-kb",
            toDefaultRequestPayload({ instanceId })
        );
        return response;
    }

    async deleteKB(instanceId: string): Promise<APIResponse<DeleteKBOutput>> {
        const response: APIResponse<DeleteKBOutput> = await RestAPI.post(
            "custom-domain",
            "/knowledge-base/delete-kb",
            toDefaultRequestPayload({ instanceId })
        );
        return response;
    }

    async getKB(instanceId: string): Promise<APIResponse<GetKBOutput>> {
        const response: APIResponse<GetKBOutput> = await RestAPI.post(
            "custom-domain",
            "/knowledge-base/get-kb",
            toDefaultRequestPayload({ instanceId })
        );
        return response;
    }

    async triggerReSync(instanceId: string): Promise<APIResponse<TriggerReSyncOutput>> {
        const response: APIResponse<TriggerReSyncOutput> = await RestAPI.post(
            "custom-domain",
            "/knowledge-base/trigger-re-sync",
            toDefaultRequestPayload({ instanceId })
        );
        return response;
    }

    async listKBSyncStatus(instanceId: string): Promise<APIResponse<ListKBSyncStatusOutput>> {
        const response: APIResponse<ListKBSyncStatusOutput> = await RestAPI.post(
            "custom-domain",
            "/knowledge-base/list-kb-sync-status",
            toDefaultRequestPayload({ instanceId })
        );
        return response;
    }

    async createKBMcpTool(instanceId: string): Promise<APIResponse<KBMcpToolOutput>> {
        const response: APIResponse<KBMcpToolOutput> = await RestAPI.post(
            "custom-domain",
            "/knowledge-base/create-kb-mcp-tool",
            toDefaultRequestPayload({ instanceId })
        );
        return response;
    }

    async deleteKBMcpTool(instanceId: string): Promise<APIResponse<KBMcpToolOutput>> {
        const response: APIResponse<KBMcpToolOutput> = await RestAPI.post(
            "custom-domain",
            "/knowledge-base/delete-kb-mcp-tool",
            toDefaultRequestPayload({ instanceId })
        );
        return response;
    }

    async getKBMcpTool(instanceId: string): Promise<APIResponse<KBMcpToolOutput>> {
        const response: APIResponse<KBMcpToolOutput> = await RestAPI.post(
            "custom-domain",
            "/knowledge-base/get-kb-mcp-tool",
            toDefaultRequestPayload({ instanceId })
        );
        return response;
    }

    async connectAITeammate(
        instanceId: string, teammateRoleArn: string
    ): Promise<APIResponse<AITeammateAccessOutput>> {
        const response: APIResponse<AITeammateAccessOutput> = await RestAPI.post(
            "custom-domain",
            "/knowledge-base/connect-ai-teammate",
            toDefaultRequestPayload({ instanceId, teammateRoleArn })
        );
        return response;
    }

    async disconnectAITeammate(
        instanceId: string, teammateRoleArn?: string
    ): Promise<APIResponse<AITeammateAccessOutput>> {
        const response: APIResponse<AITeammateAccessOutput> = await RestAPI.post(
            "custom-domain",
            "/knowledge-base/disconnect-ai-teammate",
            toDefaultRequestPayload(teammateRoleArn ? { instanceId, teammateRoleArn } : { instanceId })
        );
        return response;
    }

    async getAITeammateAccess(instanceId: string): Promise<APIResponse<AITeammateAccessOutput>> {
        const response: APIResponse<AITeammateAccessOutput> = await RestAPI.post(
            "custom-domain",
            "/knowledge-base/get-ai-teammate-access",
            toDefaultRequestPayload({ instanceId })
        );
        return response;
    }
}
